import React, { useMemo } from 'react';

import { useLocation, useNavigate } from 'react-router';

import { Alert, Box, CircularProgress, Typography } from '@mui/material';

import csAgents from '~/components/Common/csAgents.json';
import type { CsAgent } from '~/components/SmsVerifyStats/smsVerifyStatsUtils';
import { getSubordinateEmails } from '~/components/SmsVerifyStats/smsVerifyStatsUtils';

import { useCsDashboardStats } from '~/hooks/apis/reporting/useCsDashboardStats';
import useUrlQuery from '~/hooks/useQuery';

import CsDashboardAgentTable from './CsDashboardAgentTable';
import CsDashboardChart from './CsDashboardChart';
import CsDashboardFilters from './CsDashboardFilters';
import {
  buildChartDataForFilteredTeam,
  buildChartDataFromServerTotals,
  buildMergedAgentRows,
  computeMaxCellCount,
  getAllDates,
} from './csDashboardUtils';
import { CS_FEATURES, CS_FEATURE_MAP } from './features/registry';

const DEFAULT_FEATURE_IDS = CS_FEATURES.map((feature) => feature.id);

export default function CsDashboardContent() {
  const navigate = useNavigate();
  const location = useLocation();
  const urlQuery = useUrlQuery();

  const selectedTeamLeader = urlQuery.get('teamLeader') || '';
  const startAt = urlQuery.get('start_at') || '';
  const endAt = urlQuery.get('end_at') || '';

  const rawFeatures = urlQuery.get('features');
  const selectedFeatureIds: string[] = rawFeatures
    ? rawFeatures.split(',').filter((id) => CS_FEATURE_MAP[id])
    : DEFAULT_FEATURE_IDS;

  function updateUrlParams(updates: Record<string, string>) {
    const params = new URLSearchParams(location.search);
    Object.entries(updates).forEach(([key, value]) => {
      if (value) {
        params.set(key, value);
      } else {
        params.delete(key);
      }
    });
    navigate({ pathname: location.pathname, search: params.toString() ? `?${params}` : '' }, { replace: true });
  }

  const { data, isLoading, error } = useCsDashboardStats({
    featureIds: selectedFeatureIds,
    startAt: startAt || undefined,
    endAt: endAt || undefined,
  });

  const allAgentRows = useMemo(() => buildMergedAgentRows(data?.result.features), [data]);

  const filteredAgentIds = useMemo<Set<string> | null>(() => {
    if (!selectedTeamLeader) return null;
    const subordinateEmails = getSubordinateEmails(selectedTeamLeader, csAgents as CsAgent[]);
    const ids = new Set<string>();
    allAgentRows.forEach((row) => {
      if (subordinateEmails.has((row.agentEmail ?? '').toLowerCase())) {
        ids.add(row.agentId);
      }
    });
    return ids;
  }, [selectedTeamLeader, allAgentRows]);

  const visibleRows = useMemo(() => {
    if (!filteredAgentIds) return allAgentRows;
    return allAgentRows.filter((row) => filteredAgentIds.has(row.agentId));
  }, [allAgentRows, filteredAgentIds]);

  const chartData = useMemo(
    () =>
      filteredAgentIds
        ? buildChartDataForFilteredTeam(selectedFeatureIds, allAgentRows, filteredAgentIds)
        : buildChartDataFromServerTotals(data?.result.features, selectedFeatureIds),
    [filteredAgentIds, selectedFeatureIds, allAgentRows, data],
  );

  const allDates = useMemo(() => getAllDates(allAgentRows, selectedFeatureIds), [allAgentRows, selectedFeatureIds]);

  const maxCellCount = useMemo(
    () => computeMaxCellCount(visibleRows, selectedFeatureIds, allDates),
    [visibleRows, selectedFeatureIds, allDates],
  );

  const teamLeaders = useMemo(() => (csAgents as CsAgent[]).map((a) => ({ email: a.Email, name: a.Name })), []);

  const chartTitle = `Daily feature usage — total across ${
    selectedTeamLeader
      ? (teamLeaders.find((l) => l.email === selectedTeamLeader)?.name ?? 'selected team')
      : 'all agents'
  }`;

  return (
    <>
      <Box mt={4}>
        <Alert severity="info" variant="outlined">
          <Typography variant="body1">
            Tracks CS agent adoption of new features. Data is sourced from Snowplow events via BigQuery and refreshed
            periodically by the analytics pipeline — expect a few hours of latency between an event firing and it
            appearing here.
          </Typography>
        </Alert>
      </Box>

      <CsDashboardFilters
        selectedFeatureIds={selectedFeatureIds}
        selectedTeamLeader={selectedTeamLeader}
        startAt={startAt}
        endAt={endAt}
        teamLeaders={teamLeaders}
        updateUrlParams={updateUrlParams}
      />

      {error && (
        <Box mt={4}>
          <Alert severity="error">
            Error loading dashboard stats: {error instanceof Error ? error.message : 'Unknown error'}
          </Alert>
        </Box>
      )}

      {isLoading && (
        <Box mt={4} display="flex" justifyContent="center">
          <CircularProgress />
        </Box>
      )}

      {!isLoading && !error && data && (
        <CsDashboardChart chartData={chartData} selectedFeatureIds={selectedFeatureIds} title={chartTitle} />
      )}

      {!isLoading && !error && data && visibleRows.length > 0 && (
        <CsDashboardAgentTable
          visibleRows={visibleRows}
          selectedFeatureIds={selectedFeatureIds}
          allDates={allDates}
          maxCellCount={maxCellCount}
        />
      )}
    </>
  );
}
